'use client';

import { c as _c } from "react/compiler-runtime";
import { useLocale } from 'next-intl';
import { useMemo, useState } from 'react';
import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';
import { useMediaQuery } from 'usehooks-ts';
import { SEARCH_PARAM_KEYS } from '@/constants/search';
import { useUAContext } from '@/context/UAContext';
import { useBrowserLocale } from '@/hooks/useBrowserLocale';
import { useSavedPages, useScrollRestorationSWR } from '@/hooks/useScrollRestoration';
import { getLandingGameScore } from '@/lib/configs';
import { adaptDeviceType, convertToDeviceType, getApiDevice, getApiSort, getDeviceValue, isGamesOnlyContentTypes, mapExploreRoomItemToRoom } from '@/lib/search';
import { seededShuffle, stringToSeed } from '@/lib/seeded-shuffle';
import { Locale } from '@/server/constants/locales';
import { ContentTypeCode, exploreRooms, getRecommendWorlds, PaginatedResponse } from '@/services/cms';
import type { WorldCardInfo } from '@/types/viverse-world-card';
import type { DeviceTypeWithAndroidIOS } from '@/types/configs';

// ≥ 1440px: 8 games per page (4 recommend + 4 explore)
// < 1440px: 6 games per page (3 recommend + 3 explore)
const GAMES_TOTAL_LARGE = 8;
const GAMES_TOTAL_SMALL = 6;
export const SWIMLANE_PAGE_LIMIT = 10;
export type AllSectionsPage = {
  games: WorldCardInfo[];
  videos: WorldCardInfo[];
  experiences: WorldCardInfo[];
};
function shuffleRooms(rooms: WorldCardInfo[], section: string, sessionSeed: number): WorldCardInfo[] {
  const seed = stringToSeed(`${sessionSeed}:${section}:${rooms.map(room => room.id).join(':')}`);
  return seededShuffle(rooms, seed);
}
function shufflePage({
  games,
  videos,
  experiences
}: AllSectionsPage, sessionSeed: number): AllSectionsPage {
  return {
    games: shuffleRooms(games, 'games', sessionSeed),
    videos: shuffleRooms(videos, 'videos', sessionSeed),
    experiences: shuffleRooms(experiences, 'experiences', sessionSeed)
  };
}
type UseExploreAllSectionsDataParams = {
  activeTag: string;
  htcToken?: string;
  sort: string | null;
  device: string | null;
};
type SectionPage = PaginatedResponse<WorldCardInfo>;
function makeGetKey(activeTag: string, sort: string | null, device: string | null, contentTypes: ContentTypeCode[], browserLocale: string, limit: number) {
  return (pageIndex: number, previousPageData: SectionPage | null) => {
    if (!browserLocale) return null;
    const params = new URLSearchParams({
      [SEARCH_PARAM_KEYS.GENRE]: activeTag,
      start: '0',
      content_types: contentTypes.join(','),
      limit: limit.toString()
    });
    if (sort) params.set(SEARCH_PARAM_KEYS.SORT, sort);
    if (device) params.set(SEARCH_PARAM_KEYS.DEVICE, device);
    if (pageIndex === 0) return `allSections?${params.toString()}`;
    if (!previousPageData?.results) return null;
    const next = previousPageData.pages?.next;
    if (!next) return null;
    const start = new URLSearchParams(next.split('?')[1]).get('start');
    params.set('start', start || '0');
    return `allSections?${params.toString()}`;
  };
}
function makeFetcher(htcToken: string | undefined, locale: Locale, browserLocale: string, limit: number) {
  return (key: string): Promise<SectionPage | undefined> => {
    const query = key.split('?')[1];
    const params = new URLSearchParams(query);
    const start = Number(params.get('start'));
    const tag = params.get(SEARCH_PARAM_KEYS.GENRE);
    const device = params.get(SEARCH_PARAM_KEYS.DEVICE);
    const contentTypes = params.get('content_types')?.split(',') as ContentTypeCode[] | undefined;
    const sort = params.get(SEARCH_PARAM_KEYS.SORT);
    const apiSort = getApiSort(sort, false);
    const apiDevice = getApiDevice(device);
    return exploreRooms({
      limit,
      start,
      site_locale: locale,
      locale: browserLocale,
      tag: tag === 'all-experiences' ? '' : tag || undefined,
      exclude_persona: true,
      sort: apiSort,
      ...(apiDevice && {
        device: apiDevice
      }),
      ...(contentTypes && {
        content_types: contentTypes
      }),
      ...(isGamesOnlyContentTypes(contentTypes) && {
        game_score: getLandingGameScore()
      })
    }, htcToken, false).then(res => {
      if (!res?.results) return undefined;
      return {
        pages: res.results.pages,
        results: res.results.results.map(mapExploreRoomItemToRoom)
      };
    });
  };
}
function dedup(rooms: WorldCardInfo[]): WorldCardInfo[] {
  return rooms.filter((room, index, self) => index === self.findIndex(r => r.id === room.id));
}
type RecommendFetcherContext = {
  device: string | null;
  deviceTypeWithAndroidAndIOS: DeviceTypeWithAndroidIOS;
  htcToken: string | undefined;
  locale: string;
  limit?: number;
};
function makeRecommendFetcher(contentTypes: ContentTypeCode[], {
  device,
  deviceTypeWithAndroidAndIOS,
  htcToken,
  locale,
  limit = 0
}: RecommendFetcherContext) {
  return () => {
    const apiDevice = device ? getApiDevice(device) : deviceTypeWithAndroidAndIOS;
    if (!apiDevice) return null;
    const adaptedDevice = adaptDeviceType(convertToDeviceType(apiDevice), deviceTypeWithAndroidAndIOS);
    return getRecommendWorlds(htcToken, {
      locale,
      device: adaptedDevice,
      content_types: contentTypes,
      limit
    }).then(res => res?.results.rooms ?? []);
  };
}
export function useExploreAllSectionsData(t0) {
  const $ = _c(56);
  const {
    activeTag,
    htcToken,
    sort,
    device
  } = t0;
  const locale = useLocale();
  const browserLocale = useBrowserLocale();
  const {
    deviceTypeWithAndroidAndIOS
  } = useUAContext();
  const effectiveDevice = device ?? getDeviceValue(deviceTypeWithAndroidAndIOS) ?? "desktop";
  const isLargeDesktop = useMediaQuery("(min-width: 1440px)");
  const saved = useSavedPages();
  const [shuffleSessionSeed] = useState(Math.random);
  const gamesPageLimit = isLargeDesktop ? GAMES_TOTAL_LARGE : GAMES_TOTAL_SMALL;
  const sortByRecommend = sort === "recommended" || !sort;
  const enableRecommendWorlds = activeTag === "all-experiences" && sortByRecommend;
  const recommendKey = enableRecommendWorlds ? [activeTag, effectiveDevice, locale, "all-sections-recommend"] : null;
  const recommendContext = {
    device: effectiveDevice,
    deviceTypeWithAndroidAndIOS,
    htcToken,
    locale
  };
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      revalidateOnFocus: false
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    data: recommendGames
  } = useSWR(recommendKey ? [...recommendKey, "games"] : null, makeRecommendFetcher(["games"], {
    ...recommendContext,
    limit: gamesPageLimit
  }), t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      revalidateOnFocus: false
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    data: recommendExperiences
  } = useSWR(recommendKey ? [...recommendKey, "experiences"] : null, makeRecommendFetcher(["experiences"], recommendContext), t2);
  let t3;
  if ($[2] !== saved.savedPages) {
    t3 = {
      revalidateFirstPage: false,
      initialSize: saved.savedPages
    };
    $[2] = saved.savedPages;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const swrOptions = t3;
  const t4 = makeGetKey(activeTag, sort, effectiveDevice, ["games"], browserLocale, gamesPageLimit);
  let t5;
  if ($[4] !== browserLocale || $[5] !== gamesPageLimit || $[6] !== htcToken || $[7] !== locale) {
    t5 = makeFetcher(htcToken, locale, browserLocale, gamesPageLimit);
    $[4] = browserLocale;
    $[5] = gamesPageLimit;
    $[6] = htcToken;
    $[7] = locale;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const {
    data: gamesData,
    isLoading: gamesLoading,
    isValidating: gamesValidating,
    size: gamesSize,
    setSize: setGamesSize
  } = useSWRInfinite(t4, t5, swrOptions);
  const t6 = makeGetKey(activeTag, sort, effectiveDevice, ["experiences"], browserLocale, SWIMLANE_PAGE_LIMIT);
  let t7;
  if ($[9] !== browserLocale || $[10] !== htcToken || $[11] !== locale) {
    t7 = makeFetcher(htcToken, locale, browserLocale, SWIMLANE_PAGE_LIMIT);
    $[9] = browserLocale;
    $[10] = htcToken;
    $[11] = locale;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const {
    data: experiencesData,
    isLoading: experiencesLoading,
    isValidating: experiencesValidating,
    size: experiencesSize,
    setSize: setExperiencesSize
  } = useSWRInfinite(t6, t7, swrOptions);
  const size = Math.max(gamesData?.length ?? 0, experiencesData?.length ?? 0);
  let t8;
  if ($[13] !== enableRecommendWorlds || $[14] !== experiencesData || $[15] !== gamesData || $[16] !== gamesPageLimit || $[17] !== recommendExperiences || $[18] !== recommendGames || $[19] !== shuffleSessionSeed || $[20] !== size || $[21] !== sortByRecommend) {
    let t9;
    if ($[23] !== enableRecommendWorlds || $[24] !== experiencesData || $[25] !== gamesData || $[26] !== gamesPageLimit || $[27] !== recommendExperiences || $[28] !== recommendGames || $[29] !== shuffleSessionSeed || $[30] !== sortByRecommend) {
      t9 = (_, i) => {
        const pageGames = gamesData?.[i]?.results ?? [];
        const pageExperiences = experiencesData?.[i]?.results ?? [];
        if (i === 0 && enableRecommendWorlds) {
          const page = {
            games: dedup([...(recommendGames ?? []), ...pageGames]).slice(0, gamesPageLimit),
            videos: [],
            experiences: dedup([...(recommendExperiences ?? []), ...pageExperiences])
          };
          return shufflePage(page, shuffleSessionSeed);
        }
        const page_0 = {
          games: pageGames,
          videos: [],
          experiences: pageExperiences
        };
        return sortByRecommend ? shufflePage(page_0, shuffleSessionSeed) : page_0;
      };
      $[23] = enableRecommendWorlds;
      $[24] = experiencesData;
      $[25] = gamesData;
      $[26] = gamesPageLimit;
      $[27] = recommendExperiences;
      $[28] = recommendGames;
      $[29] = shuffleSessionSeed;
      $[30] = sortByRecommend;
      $[31] = t9;
    } else {
      t9 = $[31];
    }
    t8 = Array.from({
      length: size
    }, t9);
    $[13] = enableRecommendWorlds;
    $[14] = experiencesData;
    $[15] = gamesData;
    $[16] = gamesPageLimit;
    $[17] = recommendExperiences;
    $[18] = recommendGames;
    $[19] = shuffleSessionSeed;
    $[20] = size;
    $[21] = sortByRecommend;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const pages = t8;
  const gamesLoadedTotal = gamesData?.[0]?.pages?.total ?? 0;
  const experiencesLoadedTotal = experiencesData?.[0]?.pages?.total ?? 0;
  let t9;
  if ($[32] !== gamesData) {
    t9 = gamesData?.flatMap(_temp).length ?? 0;
    $[32] = gamesData;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  const loadedGames = t9;
  let t10;
  if ($[34] !== experiencesData) {
    t10 = experiencesData?.flatMap(_temp2).length ?? 0;
    $[34] = experiencesData;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  const loadedExperiences = t10;
  const enableLoadMore = loadedGames < gamesLoadedTotal || loadedExperiences < experiencesLoadedTotal;
  const isLoading = gamesLoading || experiencesLoading;
  const isValidating = gamesValidating || experiencesValidating;
  const size_0 = Math.min(gamesSize, experiencesSize);
  let t11;
  if ($[36] !== setExperiencesSize || $[37] !== setGamesSize) {
    t11 = nextSize => {
      setGamesSize(nextSize);
      setExperiencesSize(nextSize);
    };
    $[36] = setExperiencesSize;
    $[37] = setGamesSize;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  const setSize = t11;
  let t12;
  if ($[39] !== isLoading || $[40] !== isValidating || $[41] !== setSize || $[42] !== size_0) {
    t12 = {
      size: size_0,
      setSize,
      isLoading,
      isValidating
    };
    $[39] = isLoading;
    $[40] = isValidating;
    $[41] = setSize;
    $[42] = size_0;
    $[43] = t12;
  } else {
    t12 = $[43];
  }
  const trackedSetSize = useScrollRestorationSWR(saved, t12);
  let t13;
  if ($[44] !== isLoading || $[45] !== isValidating || $[46] !== size_0 || $[47] !== trackedSetSize) {
    t13 = {
      isLoading,
      isValidating,
      setSize: trackedSetSize,
      size: size_0
    };
    $[44] = isLoading;
    $[45] = isValidating;
    $[46] = size_0;
    $[47] = trackedSetSize;
    $[48] = t13;
  } else {
    t13 = $[48];
  }
  let t14;
  if ($[49] !== enableLoadMore || $[50] !== gamesPageLimit || $[51] !== isLoading || $[52] !== isValidating || $[53] !== pages || $[54] !== t13) {
    t14 = {
      pages,
      gamesPageLimit,
      enableLoadMore,
      isLoading,
      isValidating,
      pagination: t13
    };
    $[49] = enableLoadMore;
    $[50] = gamesPageLimit;
    $[51] = isLoading;
    $[52] = isValidating;
    $[53] = pages;
    $[54] = t13;
    $[55] = t14;
  } else {
    t14 = $[55];
  }
  return t14;
}
function _temp2(p_0) {
  return p_0?.results ?? [];
}
function _temp(p) {
  return p?.results ?? [];
}